/**
 * Centralized Analytics Error Logger
 *
 * This utility provides a consistent way to handle analytics errors:
 * - Development: Full console logging for debugging
 * - Production: Silent failures with optional backend error logging
 * - Browser extension errors: Always silently ignored
 */

// Log levels
export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

// Error categories for backend tracking
export enum ErrorCategory {
  NETWORK = 'network',
  API = 'api',
  BROWSER_EXTENSION = 'browser_extension',
  VALIDATION = 'validation',
  UNKNOWN = 'unknown'
}

// Analytics error interface
export interface AnalyticsError {
  category: ErrorCategory;
  level: LogLevel;
  message: string;
  error?: Error;
  context?: Record<string, any>;
  timestamp: Date;
}

// In-memory error buffer for batch sending (max 100 errors)
const errorBuffer: AnalyticsError[] = [];
const MAX_BUFFER_SIZE = 100;

// Configuration
let config = {
  logToConsole: process.env.NODE_ENV === 'development',
  logToBackend: false, // Set to true to send errors to backend
  maxBufferSize: MAX_BUFFER_SIZE,
  silentBrowserExtensionErrors: true
};

/**
 * Configure the analytics logger
 */
export function configureAnalyticsLogger(options: Partial<typeof config>): void {
  config = { ...config, ...options };
}

/**
 * Detect if an error is caused by a browser extension
 */
export function isBrowserExtensionError(error: Error | string | unknown): boolean {
  const errorStr = error?.toString().toLowerCase() || '';

  // Check for common extension-related patterns
  const extensionPatterns = [
    'chrome-extension://',
    'moz-extension://',
    'webkit-startup-composite-script',
    'extension context',
    'non-extension context'
  ];

  return extensionPatterns.some(pattern => errorStr.includes(pattern));
}

/**
 * Categorize an error
 */
function categorizeError(error: Error | string | unknown): ErrorCategory {
  if (isBrowserExtensionError(error)) {
    return ErrorCategory.BROWSER_EXTENSION;
  }

  const errorStr = error?.toString().toLowerCase() || '';

  if (errorStr.includes('network') || errorStr.includes('fetch') || errorStr.includes('timeout')) {
    return ErrorCategory.NETWORK;
  }

  if (errorStr.includes('api') || errorStr.includes('http')) {
    return ErrorCategory.API;
  }

  if (errorStr.includes('validation') || errorStr.includes('invalid')) {
    return ErrorCategory.VALIDATION;
  }

  return ErrorCategory.UNKNOWN;
}

/**
 * Log an analytics error
 */
export function logAnalyticsError(
  message: string,
  error?: Error | string | unknown,
  context?: Record<string, any>,
  level: LogLevel = LogLevel.ERROR
): void {
  const category = categorizeError(error);

  // Skip browser extension errors if configured to do so
  if (category === ErrorCategory.BROWSER_EXTENSION && config.silentBrowserExtensionErrors) {
    return; // Silently ignore extension errors
  }

  const analyticsError: AnalyticsError = {
    category,
    level,
    message,
    error: error instanceof Error ? error : undefined,
    context,
    timestamp: new Date()
  };

  // Console logging in development
  if (config.logToConsole) {
    const prefix = `[ANALYTICS-${level}] ${category}`;
    const details = { error, context };

    switch (level) {
      case LogLevel.DEBUG:
        console.debug(prefix, message, details);
        break;
      case LogLevel.INFO:
        console.info(prefix, message, details);
        break;
      case LogLevel.WARN:
        console.warn(prefix, message, details);
        break;
      case LogLevel.ERROR:
        console.error(prefix, message, details);
        break;
    }
  }

  // Backend logging (buffered)
  if (config.logToBackend) {
    bufferError(analyticsError);
  }
}

/**
 * Buffer error for batch sending to backend
 */
function bufferError(error: AnalyticsError): void {
  errorBuffer.push(error);

  if (errorBuffer.length >= config.maxBufferSize) {
    flushErrorBuffer();
  }
}

/**
 * Flush error buffer to backend
 */
async function flushErrorBuffer(): Promise<void> {
  if (errorBuffer.length === 0) return;

  const errorsToSend = [...errorBuffer];
  errorBuffer.length = 0; // Clear buffer

  try {
    await fetch('/api/analytics/error-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ errors: errorsToSend }),
      keepalive: true
    });
  } catch (fetchError) {
    // If we can't send error logs, don't create more errors
    // Put them back in the buffer
    errorBuffer.unshift(...errorsToSend);
  }
}

/**
 * Get buffered errors (useful for debugging)
 */
export function getBufferedErrors(): AnalyticsError[] {
  return [...errorBuffer];
}

/**
 * Clear buffered errors
 */
export function clearBufferedErrors(): void {
  errorBuffer.length = 0;
}

/**
 * Convenience method for network errors
 */
export function logNetworkError(message: string, error?: Error | unknown, context?: Record<string, any>): void {
  logAnalyticsError(message, error, context, LogLevel.ERROR);
}

/**
 * Convenience method for API errors
 */
export function logApiError(message: string, error?: Error | unknown, context?: Record<string, any>): void {
  logAnalyticsError(message, error, context, LogLevel.ERROR);
}

/**
 * Convenience method for warnings
 */
export function logAnalyticsWarning(message: string, context?: Record<string, any>): void {
  logAnalyticsError(message, undefined, context, LogLevel.WARN);
}

/**
 * Convenience method for debug info
 */
export function logAnalyticsDebug(message: string, context?: Record<string, any>): void {
  logAnalyticsError(message, undefined, context, LogLevel.DEBUG);
}

/**
 * Flush any remaining errors on page unload
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (errorBuffer.length > 0) {
      flushErrorBuffer();
    }
  });
}
